import { inject } from '@angular/core';
import { patchState, signalStoreFeature, type, withMethods } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { exhaustMap, pipe, tap } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { IHolidayState } from './holiday.state';
import { withHolidayStateMethods } from './holiday.methods.state';
import { HolidayService } from '../../services/holiday/holiday.service';
import { NotificationService } from '../../services/notification/notification.service';
import { CreateHolidayApi, EditHolidayApi, HolidayApi } from '../../models/holiday/holiday-api.model';

export const withHolidayApiMethods = signalStoreFeature(
  { state: type<IHolidayState>() },
  withHolidayStateMethods,
  withMethods((store) => {
    const holidayService = inject(HolidayService);
    const notificationService = inject(NotificationService);

    return {
      loadHolidays: rxMethod<number | undefined>(
        pipe(
          tap(() => patchState(store, { loading: true, error: null })),
          exhaustMap((year) =>
            holidayService.getHolidays(year).pipe(
              tapResponse({
                next: (res) => store.setHolidays(res.holidays),
                error: (err: HttpErrorResponse) => {
                  const msg = err.error?.message ?? err.message;
                  store.setError(msg);
                  notificationService.notifyError('Error', msg);
                },
              })
            )
          )
        )
      ),

      createHoliday: rxMethod<{ payload: CreateHolidayApi; onSuccess?: () => void }>(
        pipe(
          tap(() => patchState(store, { loading: true, error: null })),
          exhaustMap(({ payload, onSuccess }) =>
            holidayService.createHoliday(payload).pipe(
              tapResponse({
                next: (res) => {
                  store.addHoliday(res.holiday);
                  notificationService.notify('success', 'Created', 'Holiday created.');
                  onSuccess?.();
                },
                error: (err: HttpErrorResponse) => {
                  const msg = err.error?.message ?? err.message;
                  store.setError(msg);
                  notificationService.notifyError('Error', msg);
                },
              })
            )
          )
        )
      ),

      editHoliday: rxMethod<{ guid: string; payload: EditHolidayApi; onSuccess?: () => void }>(
        pipe(
          tap(() => patchState(store, { loading: true, error: null })),
          exhaustMap(({ guid, payload, onSuccess }) =>
            holidayService.editHoliday(guid, payload).pipe(
              tapResponse({
                next: (res) => {
                  store.updateHoliday(res.holiday);
                  notificationService.notify('success', 'Updated', 'Holiday updated.');
                  onSuccess?.();
                },
                error: (err: HttpErrorResponse) => {
                  const msg = err.error?.message ?? err.message;
                  store.setError(msg);
                  notificationService.notifyError('Error', msg);
                },
              })
            )
          )
        )
      ),

      toggleHolidayActive: rxMethod<{ holiday: HolidayApi; onSuccess?: () => void }>(
        pipe(
          tap(() => patchState(store, { loading: true, error: null })),
          exhaustMap(({ holiday, onSuccess }) => {
            const payload: EditHolidayApi = {
              name: holiday.name,
              date: holiday.date,
              holidayType: holiday.holidayType,
              scope: holiday.scope,
              regionCode: holiday.regionCode,
              city: holiday.city,
              description: holiday.description,
              rate: holiday.rate,
              recurringAnnually: holiday.recurringAnnually,
              isActive: !holiday.isActive,
            };
            return holidayService.editHoliday(holiday.holidayGuid, payload).pipe(
              tapResponse({
                next: (res) => {
                  store.updateHoliday(res.holiday);
                  onSuccess?.();
                },
                error: (err: HttpErrorResponse) => {
                  const msg = err.error?.message ?? err.message;
                  store.setError(msg);
                  notificationService.notifyError('Error', msg);
                },
              })
            );
          })
        )
      ),

      deleteHoliday: rxMethod<{ guid: string; onSuccess?: () => void }>(
        pipe(
          tap(() => patchState(store, { loading: true, error: null })),
          exhaustMap(({ guid, onSuccess }) =>
            holidayService.deleteHoliday(guid).pipe(
              tapResponse({
                next: () => {
                  store.removeHoliday(guid);
                  notificationService.notify('success', 'Deleted', 'Holiday deleted.');
                  onSuccess?.();
                },
                error: (err: HttpErrorResponse) => {
                  const msg = err.error?.message ?? err.message;
                  store.setError(msg);
                  notificationService.notifyError('Error', msg);
                },
              })
            )
          )
        )
      ),
    };
  })
);
